import { createElement, type ReactNode } from "react";
import { Link } from "react-router";
import { FEATURE_FLAGS } from "./feature-flag-constants";
import SitesIcon from "../assets/icons/sites.svg?react";
import InvestigatorIcon from "../assets/icons/assignment_ind.svg?react";
import AskVivoIcon from "../assets/icons/ask_vivo2.svg?react";
import ClinicalIcon from "../assets/icons/clinical.svg?react";
import DataIcon from "../assets/icons/data.svg?react";
import PlanningIcon from "../assets/icons/app_checklist.svg?react";

type PortfolioMenuItem =
  | {
      type: "divider";
      key: string;
      featureflag?: never;
    }
  | {
      key: string;
      featureflag?: string;
      icon?: ReactNode;
      label: ReactNode;
      type?: never;
      children?: PortfolioMenuItem[];
    };

const askVivoItem: PortfolioMenuItem = {
  key: "/askvivo",
  featureflag: FEATURE_FLAGS.PORT_ASKVIVO,
  icon: createElement(AskVivoIcon),
  label: <Link to="askvivo">Ask Vivo</Link>,
};

const trialsItem: PortfolioMenuItem = {
  key: "/trials",
  icon: createElement(ClinicalIcon),
  label: <Link to="trials">Trials</Link>,
};

const sitesItem: PortfolioMenuItem = {
  key: "/site-profiles",
  featureflag: FEATURE_FLAGS.PORT_SITE_PROFILES,
  icon: createElement(SitesIcon),
  label: <Link to="site-profiles">Sites</Link>,
};

const investigatorsItem: PortfolioMenuItem = {
  key: "/investigators",
  featureflag: FEATURE_FLAGS.PORT_INVESTIGATORS,
  icon: createElement(InvestigatorIcon),
  label: <Link to="investigators">Investigators</Link>,
};

// app_checklist.svg hardcodes fill="white"; force currentColor so the icon dims
// like its siblings when unselected.
const planningIcon = createElement(PlanningIcon, {
  className: "[&_path]:fill-current",
});

const planningItem: PortfolioMenuItem = {
  key: "/planning",
  featureflag: FEATURE_FLAGS.TRIAL_PLANNING,
  icon: planningIcon,
  label: <Link to="planning">Planning</Link>,
};

const scenariosItem: PortfolioMenuItem = {
  ...planningItem,
  label: <Link to="planning">Scenarios</Link>,
};

const externalEvidenceChild: PortfolioMenuItem = {
  key: "/data/external",
  featureflag: FEATURE_FLAGS.EXTERNAL_EVIDENCE,
  label: <Link to="data/external/studies">External</Link>,
};

const dataItem: PortfolioMenuItem = {
  key: "/data",
  icon: createElement(DataIcon),
  label: <span>Data</span>,
  children: [
    {
      key: "/data/connections",
      label: <Link to="data/connections">Connections</Link>,
    },
    {
      key: "/data/mastery",
      label: <Link to="data/mastery">Mastery</Link>,
    },
    externalEvidenceChild,
  ],
};

// Site-first Data section: the mastery review queue gets its own entry rather
// than hiding behind a button inside the analytics page, because data stewards
// live in the queue and reach for it directly.
const siteFirstDataItem: PortfolioMenuItem = {
  ...dataItem,
  children: [
    {
      key: "/data/connections",
      label: <Link to="data/connections">Connections</Link>,
    },
    {
      key: "/data/mastery",
      label: <Link to="data/mastery">Mastery Overview</Link>,
    },
    {
      key: "/data/mastery/queue",
      label: <Link to="data/mastery/queue">Mastery Queue</Link>,
    },
    externalEvidenceChild,
  ],
};

/**
 * The portfolio nav.
 *
 * `siteFirst` (SITE_FIRST_NAV) reorders it around sites rather than trials —
 * Sites and Investigators lead, Trials follows them, Planning reads
 * "Scenarios", and Data lists both mastery surfaces. Tenants without the flag
 * get the original order and labels unchanged.
 */
export const getPortfolioMenuItems = (
  siteFirst = false,
): PortfolioMenuItem[] =>
  siteFirst
    ? [
        askVivoItem,
        { type: "divider", key: "divider-0" },
        sitesItem,
        investigatorsItem,
        { type: "divider", key: "divider-2" },
        trialsItem,
        { type: "divider", key: "divider-3" },
        scenariosItem,
        siteFirstDataItem,
      ]
    : [
        askVivoItem,
        { type: "divider", key: "divider-0" },
        trialsItem,
        { type: "divider", key: "divider-2" },
        sitesItem,
        investigatorsItem,
        { type: "divider", key: "divider-3" },
        planningItem,
        dataItem,
      ];
